// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import NotFoundPage from '../pages/NotFoundPage';

const STORAGE_KEY = 'hv_active_connection';

function getStoredConnectionId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

function buildProxyUrl(connectionId, loc) {
  const pathBase = `/api/remote/http-view/${connectionId}${loc.pathname}`;
  const params = new URLSearchParams(loc.search.replace(/^\?/, ''));
  // Auth rides the _hv_token HttpOnly cookie on this same-site top-level nav — no
  // JWT in the URL (pentest: keep the token out of access logs / history / Referer).
  // This mirrors nginx's existing cookie-only top-level bounce for device paths.
  params.set('_k', String(Date.now()));
  // A top-level navigation has no Try Again, so it must keep the proxy's connect
  // retries. Drop the viewer modal's fast-fail marker if the leaked URL inherited
  // it — the incoming query is forwarded wholesale just above.
  params.delete('_hv_ff');
  const q = params.toString();
  return `${pathBase}${q ? `?${q}` : ''}${loc.hash || ''}`;
}

/**
 * Allowlist of first path segments that belong to the Starfleet dashboard.
 * Everything NOT in this set is assumed to be a leaked device navigation
 * when an http-view session is active, and gets redirected through the proxy.
 *
 * This is the opposite of the old blocklist approach — we no longer need to
 * enumerate every possible device URL pattern. Any new dashboard route just
 * needs to be added here (and if forgotten, the worst case is a redirect
 * that self-corrects once you add it).
 */
const STARFLEET_ROUTE_SEGMENTS = new Set([
  '', // index route
  'login',
  'regions',
  'stargates',
  'debug',
  'network-metrics',
  'mass-firmware-update',
  'licenses',
  'security',
  'remote-admin',
  'remote-connections',
  'guacamole-admin',
  'guacamole-connections',
  'registry',
  'api-docs',
  'mqtt-connect',
  'host-metrics',
  'topology',
  'remote-loading',
  'remote-desktop',
  'fleet-chat',
]);

/**
 * Returns true when the path is a known Starfleet dashboard route.
 * Paths under /api/ and /static/ are also considered "ours" (handled by
 * nginx / backend, not React Router) and must NOT be redirected.
 */
export function isKnownStarfleetPath(pathname) {
  const p = (pathname || '/').toLowerCase();
  if (p === '/') return true;
  if (p.startsWith('/api/') || p.startsWith('/static/')) return true;
  const firstSeg = p.split('/')[1] || '';
  return STARFLEET_ROUTE_SEGMENTS.has(firstSeg);
}

function HardRedirectToProxy() {
  const location = useLocation();
  const id = getStoredConnectionId();

  useEffect(() => {
    if (id) {
      const url = buildProxyUrl(id, location);
      window.location.replace(url);
    }
  }, [id, location.pathname, location.search, location.hash]);

  if (!id) {
    return <NotFoundPage />;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: '#f5f5f5',
      }}
    />
  );
}

/**
 * Explicit leak routes placed outside Layout (wuilib/*, home/*, rui/*, etc.).
 */
export default function HttpViewLeakRedirect() {
  return <HardRedirectToProxy />;
}

/**
 * Catch-all inside Layout: if the path is NOT a known Starfleet route and
 * there is an active http-view connection, redirect to the proxy.
 * This guarantees ALL device URLs are caught without maintaining a blocklist.
 */
export function HttpViewNotFoundOrRecover() {
  const location = useLocation();
  const id = getStoredConnectionId();
  if (id && !isKnownStarfleetPath(location.pathname)) {
    return <HardRedirectToProxy />;
  }
  return <NotFoundPage />;
}
